import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, Clock, Brain, Users, Heart, CheckCircle2,
  AlertTriangle, Play, RotateCcw, Sparkles
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { track } from "../lib/analytics";

const PER_QUESTION_SECONDS = 30;
const SECTIONS = ["critical", "personality", "eq"];

const SECTION_META = {
  critical: { icon: Brain, color: "primary", key: "critical" },
  personality: { icon: Users, color: "primary", key: "personality" },
  eq: { icon: Heart, color: "accent", key: "eq" },
};

// Phases: "sec-intro" -> "question" -> ... -> "review" -> "retry" -> "unlock" -> submit
export default function Test() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [allQuestions, setAllQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // qid -> ans index
  const [phase, setPhase] = useState("loading"); // loading | sec-intro | question | review | retry-intro | retry | unlock | submitting
  const [sectionIndex, setSectionIndex] = useState(0); // 0..2
  const [qIdxInSection, setQIdxInSection] = useState(0);
  const [timeLeft, setTimeLeft] = useState(PER_QUESTION_SECONDS);
  const [retryQueue, setRetryQueue] = useState([]); // list of qids to retry
  const [retryIdx, setRetryIdx] = useState(0);
  const startedRef = useRef(Date.now());
  const submittingRef = useRef(false);
  const advancingRef = useRef(false);
  const bootRef = useRef(false);
  const tickRef = useRef(null);

  // group questions per section (preserves original order)
  const grouped = useMemo(() => {
    const g = { critical: [], personality: [], eq: [] };
    for (const q of allQuestions) {
      if (g[q.section]) g[q.section].push(q);
    }
    return g;
  }, [allQuestions]);

  // load questions when lang changes
  useEffect(() => {
    let mounted = true;
    const lang = i18n.language?.startsWith("hi") ? "hi" : "en";
    (async () => {
      try {
        if (!sessionId) {
          if (bootRef.current) return;
          bootRef.current = true;
          const [qResp, sResp] = await Promise.all([
            api.get(`/questions?lang=${lang}`),
            api.post("/test/start"),
          ]);
          setAllQuestions(qResp.data);
          setSessionId(sResp.data.session_id);
          track("assessment_started", { session_id: sResp.data.session_id });
          startedRef.current = Date.now();
          setPhase("sec-intro");
        } else {
          const qResp = await api.get(`/questions?lang=${lang}`);
          if (!mounted) return;
          setAllQuestions(qResp.data);
        }
      } catch (e) {
        toast.error("Could not start the test");
        navigate("/auth");
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [i18n.language]);

  // Compute unanswered per section
  const unansweredBySection = useMemo(() => {
    const out = { critical: [], personality: [], eq: [] };
    for (const q of allQuestions) {
      if (answers[q.id] === undefined) out[q.section].push(q);
    }
    return out;
  }, [allQuestions, answers]);

  const totalUnanswered = Object.values(unansweredBySection).reduce((a, b) => a + b.length, 0);

  // Current question based on phase
  const currentSectionKey = SECTIONS[sectionIndex];
  const currentQuestion =
    phase === "question"
      ? grouped[currentSectionKey]?.[qIdxInSection]
      : phase === "retry"
      ? allQuestions.find((q) => q.id === retryQueue[retryIdx])
      : null;

  // Timer management
  const clearTimer = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const advance = useCallback(() => {
    clearTimer();
    setTimeLeft(PER_QUESTION_SECONDS);

    if (phase === "question") {
      const list = grouped[currentSectionKey] || [];
      if (qIdxInSection + 1 < list.length) {
        setQIdxInSection((v) => v + 1);
      } else {
        // section done
        if (sectionIndex + 1 < SECTIONS.length) {
          setSectionIndex((s) => s + 1);
          setQIdxInSection(0);
          setPhase("sec-intro");
        } else {
          // all sections done → review or submit
          setPhase("review");
        }
      }
    } else if (phase === "retry") {
      if (retryIdx + 1 < retryQueue.length) {
        setRetryIdx((v) => v + 1);
      } else {
        // retry pass done → back to review to check if all answered
        setPhase("review");
      }
    }
    // eslint-disable-next-line
  }, [phase, qIdxInSection, sectionIndex, grouped, currentSectionKey, retryIdx, retryQueue]);

  // per-question countdown
  useEffect(() => {
    if (phase !== "question" && phase !== "retry") { clearTimer(); return; }
    if (!currentQuestion) return;
    clearTimer();
    setTimeLeft(PER_QUESTION_SECONDS);
    tickRef.current = setInterval(() => {
      setTimeLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return clearTimer;
    // eslint-disable-next-line
  }, [phase, currentQuestion?.id]);

  // time's up → auto-advance (side effect kept out of the state updater; StrictMode-safe)
  useEffect(() => {
    if (timeLeft !== 0) return;
    if (phase !== "question" && phase !== "retry") return;
    clearTimer();
    const id = setTimeout(advance, 60);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [timeLeft]);

  const startCurrentSection = () => setPhase("question");

  const startRetry = () => {
    const q = [];
    SECTIONS.forEach((s) => unansweredBySection[s].forEach((it) => q.push(it.id)));
    if (q.length === 0) { setPhase("unlock"); return; }
    setRetryQueue(q);
    setRetryIdx(0);
    setPhase("retry-intro");
  };

  const beginRetryQuestions = () => setPhase("retry");

  const onSelect = (i) => {
    if (!currentQuestion || advancingRef.current) return;
    advancingRef.current = true;
    setAnswers((a) => ({ ...a, [currentQuestion.id]: i }));
    // Small delay so user sees the selected state, then auto-advance
    setTimeout(() => { advancingRef.current = false; advance(); }, 220);
  };

  const submitAll = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPhase("submitting");
    try {
      const responses = Object.entries(answers).map(([question_id, answer_index]) => ({
        question_id, answer_index,
      }));
      const duration = Math.round((Date.now() - startedRef.current) / 1000);
      await api.post("/test/submit", { session_id: sessionId, responses, duration_seconds: duration });
      track("assessment_completed", { session_id: sessionId, duration_seconds: duration });
      toast.success(t("test.complete_toast"));
      navigate(`/result/${sessionId}`);
    } catch (e) {
      submittingRef.current = false;
      toast.error(e.response?.data?.detail || "Submission failed");
      setPhase("review");
    }
  };

  // ---------------- render helpers ----------------
  if (phase === "loading" || allQuestions.length === 0) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center">
          <Brain className="w-8 h-8 mx-auto text-primary animate-pulse" />
          <p className="mt-3 text-sm text-muted-foreground">{t("test.loading")}</p>
        </div>
      </div>
    );
  }

  const HeaderBar = () => (
    <header className="border-b border-border bg-white sticky top-0 z-30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
            <Brain className="w-4 h-4" />
          </div>
          <span className="font-display font-extrabold">BharatIQ</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
        </div>
      </div>
    </header>
  );

  // ===== PHASE: SECTION INTRO =====
  if (phase === "sec-intro") {
    const key = currentSectionKey;
    const meta = SECTION_META[key];
    const Icon = meta.icon;
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <HeaderBar />
        <main className="flex-1 grid place-items-center px-4 sm:px-6 py-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-2xl rounded-2xl border border-border bg-white p-8 sm:p-12 shadow-sm"
            data-testid={`section-intro-${key}`}
          >
            <div className="label-eyebrow font-bold">
              {t("test.section_progress", { i: sectionIndex + 1, total: SECTIONS.length })}
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl grid place-items-center ${
                meta.color === "accent" ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"
              }`}>
                <Icon className="w-7 h-7" />
              </div>
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                  {t(`test.section_intro.${key}.title`)}
                </h1>
                <div className="mt-1 text-sm font-semibold text-primary/70">
                  {t(`test.section_intro.${key}.subtitle`)}
                </div>
              </div>
            </div>
            <p className="mt-6 text-base sm:text-lg text-foreground/80 leading-relaxed font-medium">
              {t(`test.section_intro.${key}.desc`)}
            </p>
            <div className="mt-5 rounded-lg bg-muted/50 border border-border p-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <div className="text-sm font-semibold text-foreground/80">
                  {t(`test.section_intro.${key}.instructions`)}
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("test.per_q_time")}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-primary">30<span className="text-base">s</span></div>
              </div>
              <div className="rounded-lg border border-border p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Questions</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-primary">{grouped[key]?.length ?? 0}</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground font-medium">{t("test.auto_next")}</div>
            <button
              onClick={startCurrentSection}
              data-testid="section-start-btn"
              className="mt-8 w-full btn-primary py-4 rounded-md font-bold text-base inline-flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4" /> {t("test.start_section")}
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  // ===== PHASE: REVIEW =====
  if (phase === "review") {
    const allDone = totalUnanswered === 0;
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <HeaderBar />
        <main className="flex-1 grid place-items-center px-4 sm:px-6 py-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="w-full max-w-2xl rounded-2xl border border-border bg-white p-8 shadow-sm" data-testid="review-screen">
            <div className={`w-14 h-14 rounded-xl grid place-items-center ${
              allDone ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
            }`}>
              {allDone ? <CheckCircle2 className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              {allDone ? t("test.all_done") : t("test.unanswered_review_title")}
            </h1>
            {!allDone && (
              <p className="mt-3 text-base text-foreground/80 font-medium">
                {t("test.unanswered_review_desc", { n: totalUnanswered })}
              </p>
            )}

            {/* Per-section breakdown */}
            <div className="mt-6 space-y-3">
              {SECTIONS.map((s) => {
                const meta = SECTION_META[s];
                const Icon = meta.icon;
                const unanswered = unansweredBySection[s]?.length ?? 0;
                const total = grouped[s]?.length ?? 0;
                const done = total - unanswered;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={s} className="rounded-lg border border-border p-4" data-testid={`review-section-${s}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-md grid place-items-center ${
                          meta.color === "accent" ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"
                        }`}><Icon className="w-5 h-5" /></div>
                        <div>
                          <div className="font-display font-bold text-base">{t(`test.section.${s}`)}</div>
                          <div className="text-xs text-muted-foreground font-medium">
                            {done} / {total} · {unanswered === 0 ? t("test.all_done") : t("test.unanswered_section", { n: unanswered, section: t(`test.section.${s}`) })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-display text-2xl font-extrabold ${unanswered === 0 ? "text-primary" : "text-accent"}`}>{pct}%</div>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full ${unanswered === 0 ? "bg-primary" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {allDone ? (
              <button onClick={submitAll} data-testid="review-unlock-btn"
                className="mt-8 w-full btn-primary py-4 rounded-md font-bold text-base inline-flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> {t("test.unlock_now")}
              </button>
            ) : (
              <button onClick={startRetry} data-testid="review-retry-btn"
                className="mt-8 w-full btn-accent py-4 rounded-md font-bold text-base inline-flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> {t("test.retry_btn", { n: totalUnanswered })}
              </button>
            )}
          </motion.div>
        </main>
      </div>
    );
  }

  // ===== PHASE: RETRY INTRO =====
  if (phase === "retry-intro") {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <HeaderBar />
        <main className="flex-1 grid place-items-center px-4 sm:px-6 py-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="w-full max-w-2xl rounded-2xl border border-border bg-white p-8 sm:p-12 shadow-sm text-center" data-testid="retry-intro">
            <div className="w-16 h-16 mx-auto rounded-xl bg-accent text-accent-foreground grid place-items-center">
              <RotateCcw className="w-8 h-8" />
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              {t("test.retrying_notice")}
            </h1>
            <p className="mt-3 text-base text-foreground/80 font-semibold">
              {t("test.one_last_pass", { n: retryQueue.length })}
            </p>
            <p className="mt-4 text-sm text-muted-foreground font-medium">{t("test.per_q_time")} · {t("test.auto_next")}</p>
            <button onClick={beginRetryQuestions} data-testid="retry-begin-btn"
              className="mt-8 w-full btn-primary py-4 rounded-md font-bold text-base inline-flex items-center justify-center gap-2">
              <Play className="w-4 h-4" /> {t("test.start_section")}
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  if (phase === "submitting") {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center">
          <Sparkles className="w-8 h-8 mx-auto text-primary animate-pulse" />
          <p className="mt-3 text-sm font-semibold text-muted-foreground">{t("test.submitting")}</p>
        </div>
      </div>
    );
  }

  // ===== PHASE: QUESTION or RETRY =====
  if (!currentQuestion) return null;

  const currentTotalInPhase =
    phase === "retry" ? retryQueue.length : grouped[currentSectionKey]?.length ?? 0;
  const currentIndexInPhase = phase === "retry" ? retryIdx : qIdxInSection;
  const sectionKey = currentQuestion.section;
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  const isLow = timeLeft <= 10;
  const isCritical = timeLeft <= 5;
  const timePct = Math.round((timeLeft / PER_QUESTION_SECONDS) * 100);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <HeaderBar />

      {/* Progress bar for current section/retry */}
      <div className="h-1 bg-muted">
        <div className="h-full bg-accent transition-all"
          style={{ width: `${Math.round(((currentIndexInPhase) / Math.max(1, currentTotalInPhase)) * 100)}%` }} />
      </div>

      {/* Section badge row */}
      <div className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-md grid place-items-center ${
              meta.color === "accent" ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"
            }`}><Icon className="w-4 h-4" /></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {phase === "retry" ? t("test.retrying_notice") : t(`test.section_intro.${sectionKey}.subtitle`)}
              </div>
              <div className="font-display font-extrabold text-sm">{t(`test.section.${sectionKey}`)}</div>
            </div>
          </div>
          <div className="text-xs font-mono font-bold text-muted-foreground">
            {t("test.question_of", { n: currentIndexInPhase + 1, total: currentTotalInPhase })}
          </div>
        </div>
      </div>

      {/* Big countdown timer */}
      <div className={`transition-colors ${
        isCritical ? "bg-destructive/10" : isLow ? "bg-accent/10" : "bg-primary/5"
      }`} data-testid="question-timer-bar">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Clock className={`w-6 h-6 ${isCritical ? "text-destructive animate-pulse" : isLow ? "text-accent" : "text-primary"}`} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-bold uppercase tracking-widest ${
                isCritical ? "text-destructive" : "text-muted-foreground"
              }`}>{t("test.time_left")}</span>
              <span
                data-testid="q-timer-seconds"
                className={`font-mono font-extrabold text-2xl sm:text-3xl tabular-nums ${
                  isCritical ? "text-destructive" : isLow ? "text-accent" : "text-primary"
                }`}
              >
                {String(timeLeft).padStart(2, "0")}<span className="text-sm">s</span>
              </span>
            </div>
            <div className="h-2 rounded-full bg-white border border-border overflow-hidden">
              <div
                data-testid="q-timer-fill"
                className={`h-full transition-[width] duration-1000 ease-linear ${
                  isCritical ? "bg-destructive" : isLow ? "bg-accent" : "bg-primary"
                }`}
                style={{ width: `${timePct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 grid place-items-center px-4 sm:px-6 py-8">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
              className="rounded-xl border border-border bg-white p-6 sm:p-10 shadow-sm"
            >
              <h2
                data-testid="test-question-text"
                className="font-display text-2xl sm:text-3xl font-extrabold leading-snug tracking-tight"
              >
                {currentQuestion.text}
              </h2>

              <div className="mt-8 space-y-3">
                {currentQuestion.options.map((opt, i) => {
                  const selected = answers[currentQuestion.id] === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => onSelect(i)}
                      data-testid={`test-option-${i}`}
                      className={`group w-full text-left px-5 py-4 rounded-md border-2 transition-all flex items-center gap-4 ${
                        selected
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border bg-white hover:border-primary/50 hover:bg-muted/40"
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-md grid place-items-center text-sm font-extrabold font-mono flex-shrink-0 ${
                          selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-base sm:text-lg text-foreground/90 leading-snug font-semibold">
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-5 flex items-center justify-end">
            <button
              type="button"
              onClick={advance}
              data-testid="test-skip-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold text-muted-foreground border border-border bg-white hover:bg-muted transition-colors"
            >
              {t("test.skip")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
